import { SWR_VERIFICATION_VERSION } from './swrVerification';
import { normalizeSWRParams, SWR_MODEL_VERSION } from './swrValidation';
import { buildWithdrawalPeriods } from './swrSchedule';
import { createSeed } from './mathUtils';
import { SWR_MACRO_CALIBRATION_VERSION } from './swrMacroModel';
import { SWR_CASH_MODEL_VERSION } from './swrCashModel';
import { SWR_ETF_CALIBRATION_VERSION, SWR_ETF_DATASET_HASH } from './swrPortfolioCalibration';
import { SWR_CONTRIBUTION_VERSION } from './swrContributions';
import { isSWRHistoricalPortfolio, SWR_HISTORICAL_METHOD_VERSION, SWR_HISTORICAL_DATA_VERSION, SWR_HISTORICAL_DATA_HASH } from './swrHistoricalPortfolio';
import { isSWRFlowTaxPortfolio, isSWRDebtPortfolio, isSWRLossFirstPolicy, SWR_FLOW_ACCOUNTING_VERSION, SWR_LOSS_FIRST_ACCOUNTING_VERSION, SWR_TAX_DATA_VERSION, SWR_TAX_DATA_HASH } from './swrTaxModel';
import { SWR_DEBT_ACCOUNTING_VERSION } from './swrDebtModel';
import { SWR_WITHDRAWAL_STRATEGY_VERSION, SWR_WITHDRAWAL_CUSTOMIZATION_VERSION } from './swrWithdrawalStrategies';

export const SWR_STRESS_VERSION = 'monthly-conditioned-stress-v2';
export const SWR_METHOD_VERSIONS = Object.freeze({
  engine: SWR_MODEL_VERSION,
  randomGenerator: 'mulberry32-uint32-v1',
  returns: 'iid-monthly-lognormal-moment-matching-v1',
  parameterUncertainty: 'normal-inverse-chi-squared-observed-or-equivalent-v2',
  portfolioCalibration: SWR_ETF_CALIBRATION_VERSION,
  portfolioDataset: SWR_ETF_DATASET_HASH,
  macroCalibration: SWR_MACRO_CALIBRATION_VERSION,
  cashRules: SWR_CASH_MODEL_VERSION,
  accounting: 'monthly-income-tax-basis-v5',
  capitalContributions: SWR_CONTRIBUTION_VERSION,
  withdrawalStrategies: SWR_WITHDRAWAL_STRATEGY_VERSION,
  withdrawalCustomization: SWR_WITHDRAWAL_CUSTOMIZATION_VERSION,
  spendingReturnSignal: 'monthly-time-weighted-gross-assets-net-cash-before-costs-v1',
  stress: SWR_STRESS_VERSION,
  incomeVerification: SWR_VERIFICATION_VERSION,
});

export function getSWRMethodVersions(input) {
  const versions = { ...SWR_METHOD_VERSIONS, withdrawalStrategies: input.withdrawalStrategy?.version || SWR_WITHDRAWAL_STRATEGY_VERSION };
  return isSWRHistoricalPortfolio(input) ? { ...versions,
    returns: input.historicalPortfolio.methodVersion || SWR_HISTORICAL_METHOD_VERSION, parameterUncertainty: 'not-applied-to-historical-bootstrap',
    historicalDataset: SWR_HISTORICAL_DATA_VERSION, historicalDatasetSha256: SWR_HISTORICAL_DATA_HASH,
    ...(isSWRFlowTaxPortfolio(input) ? { accounting: isSWRDebtPortfolio(input) ? SWR_DEBT_ACCOUNTING_VERSION : isSWRLossFirstPolicy(input) ? SWR_LOSS_FIRST_ACCOUNTING_VERSION : SWR_FLOW_ACCOUNTING_VERSION,
      taxDataset: SWR_TAX_DATA_VERSION, taxDatasetSha256: SWR_TAX_DATA_HASH } : {}),
    macroCalibration: 'joint-selected-assets-and-eurostat-hicp-v1' } : versions;
}

// Stable, explicit representations are retained, not only collision-prone short hashes.
export const stableSWRStringify = value => JSON.stringify(value, (_, item) => (
  item && typeof item === 'object' && !Array.isArray(item)
    ? Object.fromEntries(Object.keys(item).sort().map(key => [key, item[key]])) : item
));

export function getSWRFinancialInputs(input) {
  const p = normalizeSWRParams(input);
  const future = p.planningMode === 'future' || p.futurePlanningEnabled === true;
  const factor = future ? p.preSimulationInflationFactor : 1;
  const annualPlan = [];
  for (const period of buildWithdrawalPeriods(p)) {
    for (let year = period.startYear; year <= period.endYear; year++) annualPlan.push(period.annualWithdrawal);
  }
  const fields = ['initialCapital', 'expectedReturn', 'annualVolatility', 'inflationRate', 'taxRate',
    'initialGainFraction', 'annualPortfolioCostRate', 'annualWealthTaxRate', 'minimumIncomeCoverage',
    'cashReturnMode', 'cashAnnualReturn', 'cashLongRunReturn', 'cashRateHalfLifeMonths',
    'cashInflationPassThrough', 'cashTaxRate', 'cashBufferFunding', 'macroMode', 'safeWithdrawalRiskMetric'];
  const model = Boolean(p.modelPortfolio || (p.portfolioPreset && p.portfolioPreset !== 'custom'));
  const uncertainty = !isSWRHistoricalPortfolio(p) && (model || Boolean(p.customReturnParameterUncertaintyEnabled
    ?? p.returnParameterUncertaintyEnabled ?? p.returnParameterUncertainty?.enabled));
  return {
    ...Object.fromEntries(fields.map(key => [key, p[key]])),
    withdrawalStrategy: p.withdrawalStrategy,
    withdrawalCustomization: p.withdrawalCustomization,
    ...(isSWRHistoricalPortfolio(p) ? { historicalPortfolio: p.historicalPortfolio } : {}),
    ...(p.simpleTaxProfile ? { simpleTaxProfile: p.simpleTaxProfile } : {}),
    annualPlan,
    capitalContributions: p.capitalContributions.map(({ month, amount }) => ({ month, amount })),
    yearsUntilStart: future ? p.yearsUntilStart : 0,
    preSimulationInflationFactor: factor,
    maxSingleYearDrawdown: p.maxSingleYearDrawdown ?? null,
    guardrailsEnabled: p.withdrawalPolicy === 'guardrails' || p.guardrailsEnabled === true,
    guardrailMinEnabled: p.guardrailMinEnabled !== false,
    guardrailMaxEnabled: p.guardrailMaxEnabled !== false,
    guardrailMinPct: p.guardrailMinPct ?? null,
    guardrailMaxPct: p.guardrailMaxPct ?? null,
    minRealWithdrawalYear0: p.minRealWithdrawalYear0 ?? null,
    maxRealWithdrawalYear0: p.maxRealWithdrawalYear0 ?? null,
    dynamicCashBufferEnabled: p.dynamicCashBufferEnabled === true,
    cashBufferMonths: p.cashBufferMonths ?? 12,
    returnParameterUncertaintyEnabled: uncertainty,
    portfolioPreset: p.portfolioPreset || p.modelPortfolio?.id || 'custom',
    portfolioCalibration: p.modelPortfolio?.calibration || null,
    stressTestDefinition: p.stressTestDefinition || null,
    stressTestMonthlyReturns: p.stressTestMonthlyReturns || null,
  };
}

export function buildSWRProvenance(input, seeds = {}) {
  const inputs = getSWRFinancialInputs(input);
  const valuationDate = input.valuationDate || new Date().toISOString().slice(0, 10);
  return {
    schemaVersion: 1,
    versions: getSWRMethodVersions(input),
    units: { currency: 'EUR', returns: 'annual-arithmetic-simple', volatility: 'annual-simple-standard-deviation',
      ...(isSWRHistoricalPortfolio(input) ? { returnStatistics: 'descriptive-nonoverlapping-12m-from-common-start', pathReturns: 'joint-monthly-historical-blocks',
        returnHaircut: 'annual-compounded-factor', inflationReference: 'annual-geometric-mean' } : {}),
      realMoneyBaseDate: valuationDate, capital: 'nominal-at-plan-start',
      withdrawals: 'net-to-beneficiary', timing: 'monthly-end-payment-start-cpi-v1' },
    inputs,
    inputSignature: stableSWRStringify(inputs),
    economicPlan: {
      initialCapital: inputs.initialCapital,
      yearsUntilStart: inputs.yearsUntilStart,
      annualWithdrawalToday: inputs.annualPlan.map(amount => amount / inputs.preSimulationInflationFactor),
      minimumIncomeCoverage: inputs.minimumIncomeCoverage,
      safeWithdrawalRiskMetric: inputs.safeWithdrawalRiskMetric,
      capitalContributions: inputs.capitalContributions,
      withdrawalCustomization: inputs.withdrawalCustomization,
    },
    seeds,
  };
}

export function assessSWRScenario(scenario) {
  const r = scenario?.results;
  const provenance = r?.provenance;
  const reasons = [];
  if (!provenance || provenance.schemaVersion !== 1) reasons.push('Provenienza del calcolo assente o non riconosciuta.');
  else {
    if (r.engineModelVersion !== SWR_MODEL_VERSION
      || stableSWRStringify(provenance.versions) !== stableSWRStringify(getSWRMethodVersions(scenario.scenarioParams))) {
      reasons.push('Il risultato usa regole o calibrazioni di una versione precedente.');
    }
    try {
      if (!scenario.scenarioParams?.valuationDate) reasons.push('Data di riferimento degli euro non registrata.');
      const expected = buildSWRProvenance(scenario.scenarioParams);
      if (provenance.inputSignature !== stableSWRStringify(provenance.inputs)
        || provenance.inputSignature !== expected.inputSignature) reasons.push('I parametri salvati non corrispondono al calcolo.');
      if (stableSWRStringify(provenance.units) !== stableSWRStringify(expected.units)) reasons.push('Unità o data di riferimento del denaro non verificabili.');
      if (stableSWRStringify(provenance.economicPlan) !== stableSWRStringify(expected.economicPlan)) reasons.push('Il calendario economico non corrisponde ai parametri.');
      const seedInput = scenario.scenarioParams.effectiveRandomSeed ?? scenario.scenarioParams.randomSeed;
      const hasRequestedSeed = seedInput !== null && seedInput !== undefined && seedInput !== '';
      // Never draw a fresh random seed while checking an intentionally blank input.
      const requestedSeed = hasRequestedSeed ? createSeed(seedInput) : null;
      const summarySeed = r.summary?.seed;
      const provenanceSeed = provenance.seeds?.market;
      if (!Number.isInteger(summarySeed) || !Number.isInteger(provenanceSeed)
        || summarySeed !== provenanceSeed
        || (hasRequestedSeed && requestedSeed !== summarySeed)) {
        reasons.push('Il seed effettivo non corrisponde al calcolo o non è verificabile.');
      }
      if (normalizeSWRParams(scenario.scenarioParams).numberOfSimulations !== r.summary?.totalSimulations) {
        reasons.push('Il numero di percorsi salvato non corrisponde al calcolo.');
      }
    } catch (e) {
      reasons.push(isSWRHistoricalPortfolio(scenario?.scenarioParams) ? e.message : 'Parametri incompleti o non validi: apri lo scenario e rivedili.');
    }
    for (const [key, label] of [['safeWithdrawalAnalysis', 'ricerca SWR'], ['stressTests', 'stress test']]) {
      const part = r[key];
      if (part && (part.engineModelVersion !== SWR_MODEL_VERSION
        || part.provenance?.inputSignature !== provenance.inputSignature
        || stableSWRStringify(part.provenance?.versions) !== stableSWRStringify(provenance.versions)
        || stableSWRStringify(part.provenance?.units) !== stableSWRStringify(provenance.units))) {
        reasons.push(`La ${label} appartiene a un calcolo diverso o non verificabile.`);
      }
      if (key === 'safeWithdrawalAnalysis' && part
        && part.riskMetric !== provenance.inputs?.safeWithdrawalRiskMetric) {
        reasons.push('Il criterio di rischio delle card SWR non corrisponde al calcolo.');
      }
      if (part && part.provenance?.seeds?.market !== provenance.seeds?.market) {
        reasons.push(`Il seed della ${label} non corrisponde al calcolo principale.`);
      }
    }
  }
  return { current: reasons.length === 0, reasons };
}

export function compareSWRScenarios(scenarios) {
  const checks = scenarios.map(assessSWRScenario);
  const reasons = [...new Set(checks.flatMap(check => check.reasons))];
  if (checks.every(check => check.current) && scenarios.length > 1) {
    const key = scenario => stableSWRStringify({ units: scenario.results.provenance.units, plan: scenario.results.provenance.economicPlan });
    if (new Set(scenarios.map(key)).size > 1) reasons.push('Capitale, decorrenza, apporti, calendario dei prelievi, minimo mensile personalizzato, quota di reddito, criterio di rischio delle card o data degli euro diversi.');
  }
  return { comparable: scenarios.length > 0 && reasons.length === 0, reasons };
}

export function sameSWRFormInputs(a, b) {
  if (!a || !b) return false;
  try {
    return stableSWRStringify(getSWRFinancialInputs(a)) === stableSWRStringify(getSWRFinancialInputs(b))
      && a.valuationDate === b.valuationDate
      && a.numberOfSimulations === b.numberOfSimulations && a.randomSeed === b.randomSeed;
  } catch { return false; }
}
